import type { FC } from 'react'
import { useState, useEffect } from 'react'

import styled from '@emotion/styled'

import type { AnimationItem } from 'lottie-web'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useIsInView from '@segment/utils/useInView'
import useLottie from '@segment/utils/useLottie/useLottie'

import jsonUrl from './assets/businesses-investing-lottie.json.data'

const data = {
  title: `Businesses investing in personalization are unlocking greater ROI.`,
  content: {
    heading: `Businesses investing in personalization stand to drive greater revenue from those that don’t.`,
    description: [
      `60% of consumers say they will become repeat buyers after a personalized shopping experience with a retailer. When Segment asked consumers the same question in 2017, only 44% said the same.`,
      `What’s more, nearly half say they’re less likely to make a purchase from a brand after an unpersonalized experience.`,
    ],
    secondHeading: `The Vintage Bar increased conversions over 33% with just one personalization tactic.`,
    button: {
      title: `Learn how`,
      href: `https://segment.com/customers/the-vintage-bar/`,
    },
  },
  animation: {
    title: `How likely are you to become a repeat buyer after a personalized shopping experience?`,
  },
}

const BusinessesInvesting: FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])
  return (
    <SectionBase variant="2Col">
      <Section.Contents alignSelf="center">
        <CopyTextBase>
          <CopyText.Heading tag="h3" variant="h4">
            {data.animation.title}
          </CopyText.Heading>
        </CopyTextBase>
        <Animation ref={containerRef} />
      </Section.Contents>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {data.title}
          </CopyText.Heading>
          <Content>
            <CopyText.Heading tag="h3" variant="h5">
              {data.content.heading}
            </CopyText.Heading>
            {data.content.description.map((text, key) => (
              <CopyText.Description key={key} tag="p" variant="pMedium">
                {text}
              </CopyText.Description>
            ))}
            <CopyText.Heading tag="h3" variant="h6">
              {data.content.secondHeading}
            </CopyText.Heading>
            <ButtonText href={data.content.button.href} iconEnd={ChevronRightIcon}>
              {data.content.button.title}
            </ButtonText>
          </Content>
        </CopyTextBase>
      </Section.Copy>
    </SectionBase>
  )
}

const Animation = styled.div`
  margin-top: 40px;
  max-width: 560px;
  aspect-ratio: 540 / 290;
`

const Content = styled.div`
  margin-top: 48px;
`

export default BusinessesInvesting
